"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight, PackageMinus, Search } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Sent, Cancelled" },
  {
    from: "Sent",
    to: "Draft, Cancelled — converting to Invoice is only allowed from Sent",
  },
  {
    from: "Cancelled",
    to: "Draft, Sent — except when stock was deducted on this challan; then Cancelled cannot be reverted",
  },
  {
    from: "Delivered",
    to: "Nothing you pick. Delivered is a stored status and shows on the tabs, but Mark As never offers it. The menu only shows Cancelled",
  },
];

const CONVERT_FROM = [
  {
    from: "Sales Order",
    when: "Open or Partially Recorded. Convert to Delivery Challan on the order row, the order detail, or the Sales Order list CONVERT menu",
    result:
      "Customer, location, items, and remaining quantity come across. Location and customer lock on the new form. Saving this challan moves the Sales Order to Partially Recorded, or Completed if remaining quantity hits zero",
  },
  {
    from: "Scratch",
    when: "Add Delivery Challan with nothing pre-filled",
    result: "A new challan. Save makes Draft; Save & Sent makes Sent so it can be invoiced straight away",
  },
  {
    from: "Duplicate",
    when: "Duplicate Document on the list or the detail page",
    result:
      "Opens Add with the same customer, items, and amounts. New challan number, today's dates, Draft, no attachments, and no Sales Order link — it is a fresh challan, not another dispatch of the same order",
  },
];

const CONVERT_TO = [
  {
    to: "Invoice",
    when: "Sent only. You need invoice create permission",
    where:
      "Convert To Invoice on the row or detail, or the list CONVERT menu (pick customer, then Sent challans, then remaining lines)",
    result:
      "Opens a new Invoice with this challan linked. The challan stays Sent until that invoice is saved. You can raise more than one invoice if quantity remains",
  },
];

const RULES = [
  {
    topic: "No ledger posting",
    rule: "A Delivery Challan never posts to the general ledger. Accounts Receivable and Sales hit when you save the Invoice created from it",
  },
  {
    topic: "Plan add-on",
    rule: "Delivery Challans are a plan feature. Creating a challan is gated by that add-on; converting a Sales Order to a challan is hidden if Delivery Challans are not on the plan. Editing, status, converting to Invoice, duplicating, and deleting are not separately gated",
  },
  {
    topic: "A primary bank account is required to create",
    rule: "If your company hasn't set up a primary bank account yet, you can't create a Delivery Challan at all — mark one under Banking → Banks with Make this primary. The bank details print on the PDF",
  },
  {
    topic: "Challan number",
    rule: "Auto-generated with a DC prefix unless you turn that off under Delivery Challan Configuration on the form. That same drawer also stores Terms & Conditions, which pre-fill every new challan. Locked after save",
  },
  {
    topic: "Division, Branch, Warehouse, Customer",
    rule: "All four are required to create. After save they are locked. Converted-from a Sales Order also locks those fields on the new form before save",
  },
  {
    topic: "Challan Type",
    rule: "Required. One of Supply of Liquid Gas, Job Work, Supply on Approval, or Others",
  },
  {
    topic: "Currency",
    rule: "Taken from the customer at creation, or from the Sales Order if this challan came from one. An exchange rate field appears when that currency differs from the company base",
  },
  {
    topic: "Stock Deduct Source is a company setting",
    rule: "Settings → Inventory Config → Stock Deduct Source. Set it to Delivery Challan for this module to take warehouse quantity. Delivery Challan appears on that list only when the add-on is on your plan. New companies default to Delivery Challan. The choice is stored on each challan at create time; later setting changes do not rewrite old challans",
  },
  {
    topic: "Stock check vs stock deduct on this challan",
    rule: "Stock Check Source can be Delivery Challan (availability only). Stock Deduct Source = Delivery Challan takes warehouse quantity on save, including a Draft. Cancel or delete (if not already Cancelled) puts deducted quantity back. Inventory Impact on detail shows the snapshot from create time",
  },
  {
    topic: "Cancelled with stock deduct cannot be reverted",
    rule: "Mark As still offers Draft and Sent on a Cancelled challan, but the save is rejected if this challan had deducted stock. Stock was released on cancel, so it cannot come back that way",
  },
  {
    topic: "Editing skips the overdue-invoice check",
    rule: "That check only runs when you first create a challan. Editing a Draft re-checks the shipping address, the credit limit, and stock, but not overdue invoices",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Sent, Delivered, and Cancelled cannot be edited. Customer and location stay locked even on a Draft",
  },
  {
    topic: "Convert to Invoice only from Sent",
    rule: "Draft, Cancelled, and Delivered cannot convert",
  },
  {
    topic: "Sent can go back to Draft",
    rule: "Mark As on a Sent challan offers Draft and Cancelled. Cancelled is not always a dead end — it can return to Draft or Sent unless stock deduct had run",
  },
  {
    topic: "Cancelling vs deleting",
    rule: "Cancel is blocked while a non-cancelled Invoice exists from this challan. Delete is stricter — blocked if any Invoice exists from it, including cancelled ones",
  },
  {
    topic: "Sales Order after cancel / delete",
    rule: "If this challan came from a Sales Order, cancel or delete recalculates remaining quantity and moves that order back to Open or Partially Recorded. The conversion link is removed on delete",
  },
  {
    topic: "Credit limit and overdue restrictions aren't Delivery Challan-specific",
    rule: "Settings → Credit limits holds the company defaults. Restriction Document is the amount check. Restriction Payment Terms Document is the overdue check — if Delivery Challan is listed, you cannot create a challan while that customer has an Overdue invoice. A customer can override those defaults on their Branches tab",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each. Editing a Draft adds new files to the existing ones; nothing is replaced unless you remove it",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a delivery challan" },
      { heading: "2. Stock check and stock deduct — Inventory Config" },
      { heading: "3. Viewing, searching, and exporting" },
      { heading: "4. Editing a delivery challan" },
      { heading: "5. Printing and downloading" },
      { heading: "6. Changing a delivery challan's status" },
      { heading: "7. Converted from — what can become a Delivery Challan" },
      { heading: "8. Converted to — what a Delivery Challan can become" },
      { heading: "9. Duplicating a delivery challan" },
      { heading: "10. Deleting a delivery challan" },
    ],
  },
  { heading: "Business rules" },
];

const FLOW = ["Quote", "Sales Order", "Delivery Challan"];

const DeductPath = ({ title, subtitle, stockAt, recommended }) => (
  <div
    className="rounded-[12px] border border-[#E5E5E5] bg-white p-4"
    style={
      recommended
        ? { borderLeftWidth: 4, borderLeftColor: "#DF5D01" }
        : undefined
    }
  >
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <p className="text-[14px] sentient-medium text-[#111827] mb-0.5">
          {title}
        </p>
        <p className="text-[12px] geist-regular text-[#6B7280] leading-relaxed">
          {subtitle}
        </p>
      </div>
      {recommended && (
        <span className="text-[10px] geist-medium uppercase tracking-wide text-[#DF5D01] bg-[#FFF7F2] rounded-full px-2 py-0.5 flex-shrink-0">
          Default
        </span>
      )}
    </div>
    <div className="flex flex-col sm:flex-row sm:items-stretch gap-2">
      {FLOW.map((label, i) => {
        const leaves = label === stockAt;
        return (
          <div key={label} className="flex sm:flex-1 items-stretch gap-2 min-w-0">
            <div
              className={`flex-1 rounded-[10px] px-3 py-2.5 border ${
                leaves
                  ? "bg-[#FFF7F2] border-[#F4C7A8]"
                  : "bg-[#FAFAFA] border-[#E5E5E5]"
              }`}
            >
              <p className="text-[12px] geist-medium text-[#111827]">{label}</p>
              <p
                className={`text-[11px] geist-regular mt-0.5 ${
                  leaves ? "text-[#DF5D01]" : "text-[#6B7280]"
                }`}
              >
                {leaves ? "Stock leaves here" : "No stock move"}
              </p>
            </div>
            {i < FLOW.length - 1 && (
              <ArrowRight
                className="hidden sm:block w-3.5 h-3.5 text-[#D4D4D4] flex-shrink-0 self-center"
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  </div>
);

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function DeliveryChallansPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Delivery Challans
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone recording that goods have left the warehouse, before
            the customer is billed
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Delivery Challan</B> is the dispatch document — items,
            quantities, and where they shipped from. It can start from scratch,
            or carry remaining quantity from an <B>Open</B> or{" "}
            <B>Partially Recorded Sales Order</B>. Once it is <B>Sent</B>,
            convert it to an <B>Invoice</B> to bill what went out. The challan
            itself does not post to the ledger. When{" "}
            <B>Settings → Inventory Config → Stock Deduct Source</B> is Delivery
            Challan, saving the challan takes stock from the warehouse.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a delivery challan
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B> —
            the rest of the form stays locked until those three are set. A
            scratch challan can pre-fill those three from{" "}
            <B>Settings → Organization defaults</B>. A few checks run
            automatically the moment you save.
          </p>
          <Definitions
            items={[
              {
                term: "Customer and Shipping Address",
                desc: (
                  <>
                    The address has to be{" "}
                    <B>one of that customer&apos;s own addresses</B>. You
                    can&apos;t pick an address that belongs to someone else.
                  </>
                ),
              },
              {
                term: "Challan Number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Delivery Challan Configuration</B> on the form (prefix and
                    next number). That same drawer stores{" "}
                    <B>Terms & Conditions</B>, which pre-fill every new challan.
                    Locked after save.
                  </>
                ),
              },
              {
                term: "Challan Type",
                desc: (
                  <>
                    Required. Choose <B>Supply of Liquid Gas</B>,{" "}
                    <B>Job Work</B>, <B>Supply on Approval</B>, or{" "}
                    <B>Others</B>.
                  </>
                ),
              },
              {
                term: "Price List",
                desc: (
                  <>
                    Optional on a scratch challan. When this challan comes from
                    a Sales Order, rates are already on the lines and the item
                    list is locked.
                  </>
                ),
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    Currency comes from the customer, or from the Sales Order
                    if this challan was converted from one. If it differs from
                    the company base (set under <B>Settings → Currencies</B>),
                    an <B>Exchange Rate</B> field appears.
                  </>
                ),
              },
              {
                term: "Items, discounts, charges, and tax",
                desc: (
                  <>
                    Line items with quantity and rate. Document discount follows{" "}
                    <B>Settings → Discounts</B> — Enable Discount, then Discount
                    Applies On <B>Item Level</B> or <B>Transaction Level</B>.
                    Extra lines such as freight come from{" "}
                    <B>Settings → Additional charges</B> assigned to Delivery
                    Challan. GST (CGST / SGST / IGST) is calculated from customer
                    state vs company state. Round-off is available on the totals.
                  </>
                ),
              },
              {
                term: "Credit limit",
                desc: (
                  <>
                    If the customer has a credit limit that covers delivery
                    challans (Restriction Document includes Delivery Challan),
                    the total can&apos;t push them over it. Defaults live under{" "}
                    <B>Settings → Credit limits</B> until the customer overrides
                    them. You&apos;ll be told the <B>limit</B>,{" "}
                    <B>current usage</B>, and <B>how much room is left</B> if
                    it would.
                  </>
                ),
              },
              {
                term: "Overdue invoices",
                desc: (
                  <>
                    If <B>Restriction Payment Terms Document</B> includes
                    Delivery Challan (on the customer, or the company default
                    under <B>Settings → Credit limits</B>), you can&apos;t
                    create a challan while that customer has an <B>Overdue</B>{" "}
                    invoice. Pay it, or remove Delivery Challan from that list.
                  </>
                ),
              },
              {
                term: "Stock check and stock deduct",
                desc: (
                  <>
                    Not a toggle on this form. They come from{" "}
                    <B>Settings → Inventory Config</B> (next section). If
                    Stock Deduct Source is Delivery Challan, quantity is taken
                    from the warehouse on save — even for a Draft.
                  </>
                ),
              },
              {
                term: "Save vs Save & Sent",
                desc: (
                  <>
                    <B>Save</B> creates a Draft. <B>Save & Sent</B> is only on
                    create — saves and marks it <B>Sent</B> in one step, so it
                    can be converted to an Invoice straight away.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            You can&apos;t create a delivery challan at all until your company
            has a <B>primary bank account</B> set up. Mark one under Banking →
            Banks with <B>Make this primary</B>. Those bank details print
            on the challan PDF, even if this particular challan never becomes
            an invoice.
          </Callout>
          <Callout type="note">
            A delivery challan does <B>not</B> post to the ledger. Nothing hits
            Accounts Receivable or Sales until you convert it and save the
            Invoice. Stock movement is separate — that follows Inventory Config
            on this module.
          </Callout>
          <Screenshot caption="Screenshot: the Add Delivery Challan form, showing Division, Branch, Warehouse, Customer, Challan Type, Save, and Save & Sent." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Stock check and stock deduct — Inventory Config
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Stock for a Delivery Challan is not switched on the challan form.
            It is a company setting under <B>Settings → Inventory Config</B>{" "}
            (the screen title is <B>Stock Source Configuration</B>). Delivery
            Challan is a real option on that screen — it is added to the
            dropdowns when the Delivery Challan add-on is on your plan.
          </p>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Two sales fields do different jobs. Check asks if quantity is
            there. Deduct is what actually takes it out of the warehouse.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <div className="border border-[#E5E5E5] rounded-[12px] p-4 bg-[#EFF8FF]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#1785B6] mb-1 flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5" />
                Stock Check Source
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                Asks: is there enough?
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Can sit on Quote, Sales Order, or Delivery Challan. Blocks
                save if the warehouse is short. Does <B>not</B> take stock.
              </p>
            </div>
            <div className="border border-[#F4C7A8] rounded-[12px] p-4 bg-[#FFF7F2]">
              <p className="text-[12px] geist-medium uppercase tracking-wide text-[#DF5D01] mb-1 flex items-center gap-1.5">
                <PackageMinus className="w-3.5 h-3.5" />
                Stock Deduct Source
              </p>
              <p className="text-[15px] sentient-medium text-[#111827] mb-1">
                Takes stock out
              </p>
              <p className="text-[13px] geist-regular text-[#374151] leading-relaxed">
                Set to Delivery Challan. The orange step below is where
                warehouse quantity actually leaves.
              </p>
            </div>
          </div>
          <div className="space-y-3 mb-4">
            <DeductPath
              recommended
              title="Where stock leaves on a Delivery Challan"
              subtitle="Save takes warehouse quantity — including a Draft."
              stockAt="Delivery Challan"
            />
          </div>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Setting
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What you pick
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What it does on a Delivery Challan
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[180px]">
                    Stock Check Source
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                    Quote, Sales Order, or Delivery Challan
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                    If Delivery Challan is selected, save is blocked when the
                    warehouse does not have enough. Nothing is taken yet —
                    this is availability only. Service items are skipped
                  </td>
                </tr>
                <tr className="border-b border-[#F5F5F5]">
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                    Stock Deduct Source
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                    Delivery Challan
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                    Save takes quantity from the warehouse (including a Draft)
                    and stores batch/lot on the lines
                  </td>
                </tr>
                <tr>
                  <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top">
                    Stock Add Source
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                    GRPO or Bill
                  </td>
                  <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                    Purchase side only. It does not apply to Delivery Challans
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "Default",
                desc: (
                  <>
                    New companies start with Stock Deduct Source ={" "}
                    <B>Delivery Challan</B>, so a challan often deducts stock
                    from the first save.
                  </>
                ),
              },
              {
                term: "How it is used on save",
                desc: (
                  <>
                    At create, Books copies the current config onto the
                    challan. If check or deduct is on for Delivery Challan,
                    availability is validated. If deduct is Delivery Challan,
                    warehouse stock is reduced and the Stock Movement tab
                    shows those batches. Editing a Draft reverses the old
                    deduction and takes the new quantities.
                  </>
                ),
              },
              {
                term: "How it is used on cancel / delete",
                desc: (
                  <>
                    If this challan deducted stock, cancel or delete (when it
                    is not already Cancelled) puts the quantity back. After
                    that, Mark As cannot bring a Cancelled challan back to
                    Draft or Sent — stock was already released.
                  </>
                ),
              },
              {
                term: "Inventory Impact on the detail page",
                desc: (
                  <>
                    The side panel shows Stock Check and Stock Deduct as
                    Enabled or Disabled for <B>this</B> challan — the snapshot
                    from when it was created, not whatever the setting is
                    today.
                  </>
                ),
              },
              {
                term: "Changing the setting later",
                desc: (
                  <>
                    You cannot switch Stock Deduct Source <B>to</B> Delivery
                    Challan while a Draft challan exists. Changing Stock Check
                    Source alone is allowed. Already-saved challans keep the
                    snapshot they were created with.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            When Stock Deduct Source is Delivery Challan, stock leaves at
            dispatch — even if you only Save as Draft.
          </Callout>
          <Screenshot caption="Screenshot: Settings → Inventory Config, Stock Source Configuration, with Stock Check Source and Stock Deduct Source set to Delivery Challan." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list and the detail page cover different needs.
          </p>
          <Definitions
            items={[
              {
                term: "Finding a challan",
                desc: (
                  <>
                    Status tabs across the top (Draft, Sent, Cancelled,
                    Delivered), plus filters for <B>date range</B>,{" "}
                    <B>status</B>, <B>customer</B>, <B>added by</B>,{" "}
                    <B>division</B>, and <B>branch</B>. Search by challan
                    number, reference, challan type, or customer name.
                  </>
                ),
              },
              {
                term: "Export",
                desc: (
                  <>
                    Export Delivery Challans from the list toolbar, using the
                    columns you currently have showing.
                  </>
                ),
              },
              {
                term: "Detail page",
                desc: (
                  <>
                    Header shows the challan number and status. Tabs cover the
                    document, attachments, <B>Document Flow</B> (linked Sales
                    Orders and Invoices), <B>Stock Movement</B>, and History.{" "}
                    <B>Converted From</B> names the Sales Order(s) if there are
                    any. <B>Converted To</B> names the Invoice(s). Inventory
                    Impact sits in the side column.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: the Delivery Challans list with status tabs, Convert menu, and row actions." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Editing a delivery challan
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Draft</B> can be edited. Sent, Delivered, and Cancelled
            lock the form.
          </p>
          <Definitions
            items={[
              {
                term: "What stays locked even on a Draft",
                desc: (
                  <>
                    Customer, Division, Branch, Warehouse, and the challan
                    number. Change items, quantities, rates, dates, challan
                    type, notes, and attachments. If the challan came from a
                    Sales Order, the item list stays locked to those lines.
                  </>
                ),
              },
              {
                term: "Stock on edit",
                desc: (
                  <>
                    If stock deduct is on, the previous deduction is reversed
                    and then taken again for the updated quantities. If stock
                    check is on, availability is re-checked.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Edit on a Draft delivery challan, with customer and location fields disabled." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Use <B>Download</B> (PDF) or <B>Print</B> on the detail header,
            next to View Customer. There is no email-send action on a delivery
            challan — <B>Sent</B> is how it becomes convertible, not a mail
            status.
          </p>
          <Screenshot caption="Screenshot: Download and Print on the delivery challan detail header, next to View Customer." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Changing a delivery challan&apos;s status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Mark Delivery Challan As</B> only offers the moves below.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As offers
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            You cannot Mark As <B>Delivered</B>. A <B>Cancelled</B> challan can
            come back to Draft or Sent only when this document did{" "}
            <B>not</B> deduct stock. If it did, revert is blocked.
          </Callout>
          <Definitions
            items={[
              {
                term: "Cancelling",
                desc: (
                  <>
                    Blocked while a <B>non-cancelled</B> Invoice exists from
                    this challan. You&apos;ll see which one. If stock deduct
                    was on, quantities go back to the warehouse. If the challan
                    came from a Sales Order, that order&apos;s remaining
                    quantity and status are recalculated.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark Delivery Challan As on a Sent challan, showing Draft and Cancelled." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Converted from — what can become a Delivery Challan
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Conversion is started on the <B>Sales Order</B> (or Duplicate /
            Add). Saving this challan is what updates the order — clicking
            Convert only opens the form.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When / where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens to the source
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_FROM.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < CONVERT_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="tip">
            A Quote does <B>not</B> convert into a Delivery Challan. Convert
            the quote to a Sales Order (or Invoice) first. You also cannot
            create a challan from a Customer Order.
          </Callout>
          <Definitions
            items={[
              {
                term: "Remaining quantity",
                desc: (
                  <>
                    You cannot dispatch more than the Sales Order still has
                    pending after other non-cancelled challans and invoices.
                    On a Partially Recorded order, convert only offers lines
                    that still have quantity left.
                  </>
                ),
              },
              {
                term: "Several orders, one challan",
                desc: (
                  <>
                    The Sales Order list CONVERT menu lets you pick a customer,
                    then more than one Open / Partially Recorded order, then
                    remaining lines — so one dispatch can cover several orders.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Converted From on a delivery challan detail, showing the linked Sales Order(s)." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Converted to — what a Delivery Challan can become
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Sent</B> challan converts, and only into an Invoice.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where you start
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_TO.map((row, i) => (
                  <tr
                    key={row.to}
                    className={
                      i < CONVERT_TO.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[150px]">
                      {row.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "List CONVERT menu",
                desc: (
                  <>
                    Convert to Invoice opens a picker: customer first, then
                    which Sent challans, then which remaining lines. Use this
                    when several challans for the same customer go on one
                    invoice.
                  </>
                ),
              },
              {
                term: "What you cannot convert to from here",
                desc: (
                  <>
                    Not a Quote, not a Sales Order, not a Credit Note, not
                    another Delivery Challan, not Production. Those are
                    separate documents.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Convert To Invoice on a Sent delivery challan." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Duplicating a delivery challan
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            From the list or the detail page, <B>Duplicate Document</B> opens
            Add Delivery Challan with the same customer, items, and amounts.
          </p>
          <Definitions
            items={[
              {
                term: "What is left off",
                desc: (
                  <>
                    A new challan number, today&apos;s dates, Draft status, no
                    attachments, and no Sales Order link. You save it as a
                    fresh challan.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Duplicate Document on the delivery challan list row actions." />

          <h3
            id="section-0-sub-9"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            10. Deleting a delivery challan
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Delete is stricter than cancel.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked when",
                desc: (
                  <>
                    Any <B>Invoice</B> exists from this challan — including
                    cancelled ones. You&apos;ll be shown which invoice is
                    holding it.
                  </>
                ),
              },
              {
                term: "Stock and Sales Orders",
                desc: (
                  <>
                    If stock deduct was on and the challan is not already
                    Cancelled, quantities go back to the warehouse. Linked
                    Sales Orders drop this challan from Converted To and
                    remaining quantity is recalculated.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a delete attempt blocked because an Invoice was already created from the delivery challan." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/sales-orders"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Sales Orders <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              An Open order can become this Delivery Challan
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/invoices"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Invoices <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              The only document a Sent challan can become
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/inventory-config"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Inventory Config <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Turns stock check and stock deduct on — Delivery Challan is an option on that screen
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
